import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
axios.defaults.withCredentials = true;
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight, Loader2, X, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  package_name: '',
  package_type: 'umrah',
  category: '',
  duration_days: 12,
  price: 0,
  description: '',
  quota: 50,
  available_quota: 50,
  departure_date: '',
  return_date: '',
  images: [],
  includes: [],
  excludes: [],
  is_active: true,
  dp_enabled: false,
  dp_type: 'percent',
  dp_percent: 30,
  dp_value: 0,
  voucher_enabled: false,
};

const PackagesManagement = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newInclude, setNewInclude] = useState('');
  const [newExclude, setNewExclude] = useState('');
  const [packageTypes, setPackageTypes] = useState([]);
  const [search, setSearch] = useState('');
  const editIdRef = useRef(null);

  // Deep-link support: /admin?tab=packages&edit=<id> opens that package's edit modal
  if (editIdRef.current === null) {
    editIdRef.current = new URLSearchParams(window.location.search).get('edit') || '';
  }

  const filtered = packages.filter(p =>
    !search || p.package_name?.toLowerCase().includes(search.toLowerCase())
  );

  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/packages/?show_all=true`);
      setPackages(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching packages:', error);
      }
      toast.error('Gagal memuat data paket');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/master/package-types`)
      .then(res => setPackageTypes(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  // Open the package named by ?edit=<id> once the list has loaded.
  // The list is paginated upstream, so the target may not be in it — fall back to
  // fetching it by id instead of silently doing nothing.
  useEffect(() => {
    if (!editIdRef.current || packages.length === 0) return;
    const id = editIdRef.current;
    editIdRef.current = '';   // consume, open once
    const target = packages.find(p => p.id === id);
    if (target) {
      handleOpen(target);
    } else {
      axios.get(`${API}/packages/${id}`, { params: { show_all: true } })
        .then(res => handleOpen(res.data))
        .catch(() => toast.error('Paket tidak ditemukan'));
    }
  }, [packages]);

  const handleOpen = (pkg = null) => {
    setNewImageUrl('');
    setNewInclude('');
    setNewExclude('');
    if (pkg) {
      setEditing(pkg);
      setFormData({
        package_name: pkg.package_name || '',
        package_type: pkg.package_type || 'umrah',
        duration_days: pkg.duration_days || 12,
        price: pkg.price || 0,
        description: pkg.description || '',
        quota: pkg.quota || 50,
        available_quota: pkg.available_quota ?? pkg.quota ?? 50,
        departure_date: pkg.departure_date || '',
        return_date: pkg.return_date || '',
        images: pkg.images || [],
        includes: pkg.includes || [],
        excludes: pkg.excludes || [],
        is_active: pkg.is_active !== undefined ? pkg.is_active : true,
      });
    } else {
      setEditing(null);
      setFormData(initialState);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        duration_days: parseInt(formData.duration_days),
        quota: parseInt(formData.quota),
        available_quota: parseInt(formData.available_quota ?? formData.quota),
      };
      if (editing) {
        await axios.put(`${API}/packages/${editing.id}`, payload);
        toast.success('Paket berhasil diupdate');
      } else {
        await axios.post(`${API}/packages/`, payload);
        toast.success('Paket berhasil ditambahkan');
      }
      setShowModal(false);
      fetchPackages();
    } catch (err) {
      toast.error(editing ? 'Gagal update paket' : 'Gagal tambah paket');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (packageId, currentStatus) => {
    try {
      await axios.put(`${API}/packages/${packageId}`, { is_active: !currentStatus });
      toast.success('Status paket berhasil diubah');
      fetchPackages();
    } catch (error) {
      toast.error('Gagal mengubah status paket');
    }
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm('Yakin ingin menghapus paket ini?')) return;
    try {
      await axios.delete(`${API}/packages/${packageId}`);
      toast.success('Paket berhasil dihapus');
      fetchPackages();
    } catch {
      toast.error('Gagal menghapus paket');
    }
  };

  const addImageUrl = () => {
    if (!newImageUrl.trim()) return;
    setFormData({ ...formData, images: [...formData.images, newImageUrl.trim()] });
    setNewImageUrl('');
  };

  const removeImageUrl = (idx) => {
    setFormData({ ...formData, images: formData.images.filter((_, i) => i !== idx) });
  };

  const addInclude = () => {
    if (!newInclude.trim()) return;
    setFormData({ ...formData, includes: [...formData.includes, newInclude.trim()] });
    setNewInclude('');
  };

  const removeInclude = (idx) => {
    setFormData({ ...formData, includes: formData.includes.filter((_, i) => i !== idx) });
  };

  const addExclude = () => {
    if (!newExclude.trim()) return;
    setFormData({ ...formData, excludes: [...formData.excludes, newExclude.trim()] });
    setNewExclude('');
  };

  const removeExclude = (idx) => {
    setFormData({ ...formData, excludes: formData.excludes.filter((_, i) => i !== idx) });
  };

  if (loading) {
    return <div className="text-center py-8">Memuat data...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold font-['Outfit']">Packages Management</h1>
        <Button className="rounded-full" onClick={() => handleOpen()}>
          <Plus className="h-4 w-4 mr-2" /> Tambah Paket
        </Button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Cari nama paket..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>Daftar Paket</CardTitle>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {packages.length === 0 ? 'Belum ada data paket' : 'Tidak ada paket cocok'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4 font-semibold">Nama Paket</th>
                    <th className="text-left p-4 font-semibold">Jenis</th>
                    <th className="text-left p-4 font-semibold">Durasi</th>
                    <th className="text-left p-4 font-semibold">Harga</th>
                    <th className="text-left p-4 font-semibold">Kuota</th>
                    <th className="text-left p-4 font-semibold">Status</th>
                    <th className="text-right p-4 font-semibold">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((pkg) => (
                    <tr key={pkg.id} className="border-b hover:bg-muted/50">
                      <td className="p-4 font-medium">{pkg.package_name}</td>
                      <td className="p-4 text-muted-foreground"><Badge>{pkg.package_type}</Badge></td>
                      <td className="p-4">{pkg.duration_days} hari</td>
                      <td className="p-4">Rp {pkg.price?.toLocaleString('id-ID')}</td>
                      <td className="p-4">
                        <Badge variant={pkg.available_quota > 0 ? 'default' : 'destructive'}>
                          {pkg.available_quota} / {pkg.quota}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge variant={pkg.is_active ? 'default' : 'secondary'}>
                          {pkg.is_active ? 'Aktif' : 'Nonaktif'}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end items-center gap-2">
                          <button type="button" onClick={() => handleToggleActive(pkg.id, pkg.is_active)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${pkg.is_active ? 'bg-primary' : 'bg-gray-300'}`}
                            title={pkg.is_active ? 'Nonaktifkan' : 'Aktifkan'}>
                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${pkg.is_active ? 'translate-x-5' : 'translate-x-1'}`} />
                          </button>
                          <Button variant="outline" size="sm" onClick={() => handleOpen(pkg)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => handleDelete(pkg.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Paket' : 'Tambah Paket Baru'}</DialogTitle>
            <DialogDescription>Lengkapi form untuk {editing ? 'mengupdate' : 'menambahkan'} paket</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              {/* Nama & Tipe */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nama Paket *</Label>
                  <Input id="name" value={formData.package_name} onChange={(e) => setFormData({...formData, package_name: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Tipe Paket</Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={formData.package_type} onChange={(e) => setFormData({...formData, package_type: e.target.value})}>
                    <option value="">-- Pilih Tipe --</option>
                    {packageTypes.map(t => (
                      <option key={t.id} value={t.code}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Kategori</Label>
                  <Input value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} placeholder="Reguler / Premium / VIP" />
                </div>
              </div>

              {/* Durasi, Harga, Kuota, Terisi */}
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="durasi">Durasi (hari)</Label>
                  <Input id="durasi" type="number" min="1" value={formData.duration_days} onChange={(e) => setFormData({...formData, duration_days: parseInt(e.target.value)})} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Harga *</Label>
                  <Input id="price" type="number" min="0" value={formData.price} onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quota">Kuota</Label>
                  <Input id="quota" type="number" min="1" value={formData.quota} onChange={(e) => setFormData({...formData, quota: parseInt(e.target.value)})} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="avail_quota">Terisi</Label>
                  <Input id="avail_quota" type="number" min="0" value={formData.available_quota} onChange={(e) => setFormData({...formData, available_quota: parseInt(e.target.value)})} />
                </div>
              </div>

              {/* Tanggal */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="departure">Tanggal Berangkat</Label>
                  <Input id="departure" type="date" value={formData.departure_date} onChange={(e) => setFormData({...formData, departure_date: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="return">Tanggal Pulang</Label>
                  <Input id="return" type="date" value={formData.return_date} onChange={(e) => setFormData({...formData, return_date: e.target.value})} />
                </div>
              </div>

              {/* Deskripsi */}
              <div className="space-y-2">
                <Label htmlFor="desc">Deskripsi</Label>
                <textarea
                  id="desc"
                  rows={4}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>

              {/* Gambar */}
              <div className="space-y-2">
                <Label>Gambar (URL)</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="https://contoh.com/gambar.jpg"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addImageUrl(); } }}
                  />
                  <Button type="button" variant="outline" onClick={addImageUrl}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.images.length > 0 && (
                  <div className="space-y-1 mt-2">
                    {formData.images.map((url, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="flex-1 truncate text-muted-foreground">{url}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeImageUrl(i)}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Termasuk */}
              <div className="space-y-2">
                <Label>Termasuk</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Contoh: Tiket Pesawat PP"
                    value={newInclude}
                    onChange={(e) => setNewInclude(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInclude(); } }}
                  />
                  <Button type="button" variant="outline" onClick={addInclude}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.includes.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {formData.includes.map((item, i) => (
                      <Badge key={i} variant="secondary" className="gap-1">
                        {item}
                        <button type="button" onClick={() => removeInclude(i)} className="hover:text-destructive">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Tidak Termasuk */}
              <div className="space-y-2">
                <Label>Tidak Termasuk</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Contoh: Paspor"
                    value={newExclude}
                    onChange={(e) => setNewExclude(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExclude(); } }}
                  />
                  <Button type="button" variant="outline" onClick={addExclude}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.excludes.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {formData.excludes.map((item, i) => (
                      <Badge key={i} variant="outline" className="gap-1">
                        {item}
                        <button type="button" onClick={() => removeExclude(i)} className="hover:text-destructive">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* DP & Voucher Settings */}
              <div className="border-t pt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Aktifkan DP untuk paket ini</Label>
                    <p className="text-xs text-muted-foreground">Jika aktif, pembeli bisa bayar DP</p>
                  </div>
                  <button type="button" onClick={() => setFormData({...formData, dp_enabled: !formData.dp_enabled})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.dp_enabled ? 'bg-primary' : 'bg-gray-300'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.dp_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                {formData.dp_enabled && (
                  <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-primary/30">
                    <div className="space-y-2">
                      <Label>Tipe DP</Label>
                      <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={formData.dp_type} onChange={(e) => setFormData({...formData, dp_type: e.target.value})}>
                        <option value="percent">Persen (%)</option>
                        <option value="nominal">Nominal (Rp)</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>{formData.dp_type === 'nominal' ? 'Nominal DP (Rp)' : 'Persen DP (%)'}</Label>
                      <Input type="number" value={formData.dp_type === 'nominal' ? formData.dp_value : formData.dp_percent}
                        onChange={(e) => setFormData({...formData, [formData.dp_type === 'nominal' ? 'dp_value' : 'dp_percent']: parseFloat(e.target.value) || 0})} />
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between border-t pt-4">
                  <div>
                    <Label>Aktifkan Voucher untuk paket ini</Label>
                    <p className="text-xs text-muted-foreground">Jika aktif, pembeli bisa pakai kode voucher</p>
                  </div>
                  <button type="button" onClick={() => setFormData({...formData, voucher_enabled: !formData.voucher_enabled})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.voucher_enabled ? 'bg-primary' : 'bg-gray-300'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.voucher_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Batal</Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editing ? 'Simpan' : 'Tambah'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesManagement;
